import { useMemo } from 'react';

import { useLocalStorage, LocalStorageKey } from '@hooks/useLocalStorage';
import { TOGGLEABLE_LAYERS } from '../mapData/toggleableLayers';
import { LayerConfig, LayerName, Layers } from '../types/types';

const getDefaultConfig = (
  layerName: LayerName,
): [LocalStorageKey, Omit<LayerConfig, 'setConfig'>] => {
  const layer = TOGGLEABLE_LAYERS[layerName];
  const isWholeLayerVisible = layer.visibleByDefault;
  const toggleableGroups = layer.visibleByDefault ? layer.defaultToggleableGroups : [];

  return [layer.localStorageKey, { isWholeLayerVisible, toggleableGroups }];
};

const fishingWatersDefaultConfig = getDefaultConfig('fishingWaters');
const spotPredictionDefaultConfig = getDefaultConfig('spotPrediction');
const catchPositionsDefaultConfig = getDefaultConfig('catchPositions');
const pointsOfInterestDefaultConfig = getDefaultConfig('pointsOfInterest');
const waypointsDefaultConfig = getDefaultConfig('waypoints');

export const useToggleableLayerConfigs = (): { layers: Layers; isLoaded: boolean } => {
  const {
    state: [fishingWatersConfig, setFishingWatersConfig],
    isLoaded: isFishingWatersConfigLoaded,
  } = useLocalStorage(...fishingWatersDefaultConfig);
  const {
    state: [spotPredictionConfig, setSpotPredictionConfig],
    isLoaded: spotPredictionConfigLoaded,
  } = useLocalStorage(...spotPredictionDefaultConfig);
  const {
    state: [catchPositionsConfig, setCatchPositionsConfig],
    isLoaded: isCatchPositionsConfigLoaded,
  } = useLocalStorage(...catchPositionsDefaultConfig);
  const {
    state: [pointsOfInterestConfig, setPointsOfInterestConfig],
    isLoaded: isPointsOfInterestConfigLoaded,
  } = useLocalStorage(...pointsOfInterestDefaultConfig);
  const {
    state: [waypointsConfig, setWaypointsConfig],
    isLoaded: isWaypointsConfigLoaded,
  } = useLocalStorage(...waypointsDefaultConfig);

  return useMemo(
    () => ({
      layers: {
        fishingWaters: { ...fishingWatersConfig, setConfig: setFishingWatersConfig },
        spotPrediction: { ...spotPredictionConfig, setConfig: setSpotPredictionConfig },
        catchPositions: { ...catchPositionsConfig, setConfig: setCatchPositionsConfig },
        pointsOfInterest: { ...pointsOfInterestConfig, setConfig: setPointsOfInterestConfig },
        waypoints: { ...waypointsConfig, setConfig: setWaypointsConfig },
      },
      isLoaded:
        isFishingWatersConfigLoaded &&
        spotPredictionConfigLoaded &&
        isCatchPositionsConfigLoaded &&
        isPointsOfInterestConfigLoaded &&
        isWaypointsConfigLoaded,
    }),
    [
      catchPositionsConfig,
      fishingWatersConfig,
      isCatchPositionsConfigLoaded,
      isFishingWatersConfigLoaded,
      isPointsOfInterestConfigLoaded,
      isWaypointsConfigLoaded,
      pointsOfInterestConfig,
      setCatchPositionsConfig,
      setFishingWatersConfig,
      setPointsOfInterestConfig,
      setSpotPredictionConfig,
      setWaypointsConfig,
      spotPredictionConfig,
      spotPredictionConfigLoaded,
      waypointsConfig,
    ],
  );
};
